import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGetScheduleQuery } from 'store/schedules/schedules';
import { Grid, Stack, Tab, Dialog, Divider } from '@mui/material';
import { NavBarContainer, NavBarTitle } from 'common/NavBar';
import { Schedule } from 'store/schedules/interfaces';
import ContentSuspense from 'common/ContentSuspense/ContentSuspense';
import NavigationBackButton from 'common/NavigationBackButton/NavigationBackButton';
import StyledTabList from 'config/material-ui/styled/TabList.styled';
import { TabContext, TabPanel } from '@mui/lab';
import SchedulePreviewCalendar from 'components/SchedulePreviewCalendar/SchedulePreviewCalendar';
import ActionOutlinedButton from 'common/ActionOutlinedButton/ActionOutlinedButton';
import EditIcon from 'assets/icons/EditIcon';
import DialogHeader from 'common/DialogHeader/DialogHeader';
import WorkScheduleCalendarProvider from 'components/AddWorkSchedule/WorkScheduleCalendar/context/WorkScheduleCalendarProvider';
import WorkScheduleCalendar from 'components/AddWorkSchedule/WorkScheduleCalendar/WorkScheduleCalendar';
import convertScheduleRanges from 'components/SchedulePreviewCalendar/convertScheduleRanges';
import { useAppDispatch } from 'store/hooks';
import api from 'store/api';

export enum WorkScheduleTabValue {
  overview = '1',
}

export default function WorkSchedulePreview() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const dispatch = useAppDispatch();

  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState(WorkScheduleTabValue.overview);
  const { id } = useParams<{ id: string }>();

  const {
    data: schedule = {} as Schedule,
    isLoading: scheduleLoading,
    isError: schedulesError,
  } = useGetScheduleQuery(id ?? '', {
    skip: !id,
  });

  const scheduleRanges = convertScheduleRanges({
    scheduleRanges: schedule.scheduleRanges ?? [],
    isRegularWeek: schedule.regular,
  });

  const handleOnClose = () => {
    setIsModalOpen(false);
    dispatch(api.util.invalidateTags(['Schedules']));
  };

  return (
    <>
      <Grid item xs={10} bgcolor="background.list">
        <NavBarContainer>
          <Stack direction="row" alignItems="center">
            <NavigationBackButton to="/work-schedules" />
            <ContentSuspense
              isError={schedulesError}
              isLoading={scheduleLoading}
            >
              <NavBarTitle>{schedule.name}</NavBarTitle>
            </ContentSuspense>
          </Stack>
          <ActionOutlinedButton
            onClick={() => setIsModalOpen(true)}
            label={t('project_tracking.edit_work_schedule')}
            endIcon={
              <EditIcon sx={{ color: 'primary.main', fontSize: '2rem' }} />
            }
          />
        </NavBarContainer>
        <TabContext value={tabValue}>
          <StyledTabList
            onChange={(event, newValue: WorkScheduleTabValue) =>
              setTabValue(newValue)
            }
            TabIndicatorProps={{ children: <span /> }}
          >
            <Tab value={WorkScheduleTabValue.overview} label="Overview" />
          </StyledTabList>
          <ContentSuspense isError={schedulesError} isLoading={scheduleLoading}>
            <TabPanel
              value={WorkScheduleTabValue.overview}
              sx={{
                p: 0,
              }}
            >
              <SchedulePreviewCalendar
                schedule={schedule}
                scheduleRanges={scheduleRanges}
              />
            </TabPanel>
          </ContentSuspense>
        </TabContext>
      </Grid>
      <Dialog
        open={isModalOpen}
        onClose={handleOnClose}
        fullWidth
        maxWidth={false}
      >
        <DialogHeader
          title={t('project_tracking.edit_work_schedule')}
          onClose={handleOnClose}
        />
        <Divider />
        <WorkScheduleCalendarProvider
          schedule={schedule}
          initWorkSchedules={scheduleRanges}
        >
          <WorkScheduleCalendar handleCloseModal={handleOnClose} />
        </WorkScheduleCalendarProvider>
      </Dialog>
    </>
  );
}
